import { elevenlabsExtractTimestamps } from "./alignment/elevenlabs-extract-timestamps";
import { replicateResembleEnhance } from "./audio-enhance/replicate-resemble-enhance";
import { elevenlabsDialogue } from "./dialogue/elevenlabs-dialogue";
import { birefnetBgRemoval } from "./image/birefnet-bg-removal";
import { cookie } from "./image/cookie";
import { gptImage15 } from "./image/gpt-image-1-5";
import { gptImage2 } from "./image/gpt-image-2";
import { grokImagine, grokImaginePro } from "./image/grok-imagine";
import { nanoBanana2 } from "./image/nano-banana-2";
import { nanoBananaPro } from "./image/nano-banana-pro";
import { runwareVectorize } from "./image/runware-vectorize";
import { seedream45 } from "./image/seedream-4-5";
import { zImage } from "./image/z-image";
import { sunoMusic } from "./music/suno-music";
import { elevenlabsSTT } from "./stt/elevenlabs-stt";
import { extractBrandStyleModel } from "./text/extract-brand-style";
import { openrouterCompletion } from "./text/openrouter-completion";
import { elevenlabsTTS } from "./tts/elevenlabs-tts";
import { minimaxSpeech28Hd } from "./tts/minimax-speech-2-8-hd";
import { briaVideoBackgroundRemoval } from "./video/bria-video-background-removal";
import { geminiOmniVideo } from "./video/gemini-omni-video";
import { grokImagineVideo } from "./video/grok-imagine-video";
import { kling26MotionControl } from "./video/kling_2_6_motion_control";
import { kling26Pro } from "./video/kling_2_6_pro";
import { kling3Pro } from "./video/kling_3_pro";
import { kling25Pro } from "./video/kling2_5_pro";
import { seedance2 } from "./video/seedance-2";
import { seedance2Extend } from "./video/seedance-2-extend";
import { seedance2Omni } from "./video/seedance-2-omni";
import { seedance2VideoEdit } from "./video/seedance-2-video-edit";
import { sora2 } from "./video/sora2";
import { sora2Pro } from "./video/sora2pro";
import { syncLipsyncV2Pro } from "./video/sync-lipsync-v2-pro";
import { topazVideoUpscale } from "./video/topaz-video-upscale";
import { veedVideoBackgroundRemoval } from "./video/veed-video-background-removal";
import { veedVideoBackgroundRemovalFast } from "./video/veed-video-background-removal-fast";
import { veo31Fast } from "./video/veo3_1-fast";
import { veo31FastExtend } from "./video/veo3_1-fast-extend";
import { replicateDemucs } from "./voice-extract/replicate-demucs";

export const modelRegistry = {
  [birefnetBgRemoval.key]: birefnetBgRemoval,
  [nanoBanana2.key]: nanoBanana2,
  [nanoBananaPro.key]: nanoBananaPro,
  [seedream45.key]: seedream45,
  [zImage.key]: zImage,
  [gptImage15.key]: gptImage15,
  [gptImage2.key]: gptImage2,
  [grokImagine.key]: grokImagine,
  [grokImaginePro.key]: grokImaginePro,
  [cookie.key]: cookie,
  [sora2Pro.key]: sora2Pro,
  [sora2.key]: sora2,
  [geminiOmniVideo.key]: geminiOmniVideo,
  [kling3Pro.key]: kling3Pro,
  [kling26MotionControl.key]: kling26MotionControl,
  [seedance2.key]: seedance2,
  [seedance2Omni.key]: seedance2Omni,
  [seedance2VideoEdit.key]: seedance2VideoEdit,
  [veo31Fast.key]: veo31Fast,
  [veo31FastExtend.key]: veo31FastExtend,
  [kling25Pro.key]: kling25Pro,
  [kling26Pro.key]: kling26Pro,
  [seedance2Extend.key]: seedance2Extend,
  [syncLipsyncV2Pro.key]: syncLipsyncV2Pro,
  [briaVideoBackgroundRemoval.key]: briaVideoBackgroundRemoval,
  [veedVideoBackgroundRemoval.key]: veedVideoBackgroundRemoval,
  [veedVideoBackgroundRemovalFast.key]: veedVideoBackgroundRemovalFast,
  [topazVideoUpscale.key]: topazVideoUpscale,
  [runwareVectorize.key]: runwareVectorize,
  [elevenlabsTTS.key]: elevenlabsTTS,
  [minimaxSpeech28Hd.key]: minimaxSpeech28Hd,
  [elevenlabsSTT.key]: elevenlabsSTT,
  [elevenlabsDialogue.key]: elevenlabsDialogue,
  [elevenlabsExtractTimestamps.key]: elevenlabsExtractTimestamps,
  [openrouterCompletion.key]: openrouterCompletion,
  [extractBrandStyleModel.key]: extractBrandStyleModel,
  [sunoMusic.key]: sunoMusic,
  [grokImagineVideo.key]: grokImagineVideo,
  [replicateResembleEnhance.key]: replicateResembleEnhance,
  [replicateDemucs.key]: replicateDemucs,
} as const;

export type ModelRegistry = typeof modelRegistry;

/** Maps a model ID to its style family key (used for style filtering). */
const modelToFamily: Record<string, string> = {
  sora2: "sora-i2v",
  sora2pro: "sora-i2v",
  kling_2_6_pro: "kling",
  kling_2_6_motion_control: "kling",
  kling_3_pro: "kling-i2v",
  "kling2_5-pro": "kling",
  "veo3_1-fast": "veo",
  "veo3_1-fast-extend": "veo",
  "nano-banana-2": "nano-banana",
  "nano-banana-pro": "nano-banana",
  "z-image": "z-image",
  "gpt-image-1-5": "gpt-image-1-5",
  "gpt-image-2": "gpt-image-2",
  "seedream-4-5": "seedream",
  "grok-imagine": "grok-imagine",
  "grok-imagine-pro": "grok-imagine",
  "seedance-2": "seedance",
  "seedance-2-extend": "seedance",
  "seedance-2-omni": "seedance",
  "seedance-2-video-edit": "seedance",
};

export function getModelFamily(modelId: string): string | undefined {
  return modelToFamily[modelId];
}

/** Check if a string is a valid model key. */
export function resolveModelKey(key: string): string | undefined {
  if (key in modelRegistry) return key;
  return undefined;
}
